import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi";
import type { UserTransformed } from "~pages/Users/types";
import type { DefaultOperationSuccessResponse } from "~types/response";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";

const useDeleteUserPermission = () => {
  const { sport, organizationId, userId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();

  return useMutation<DefaultOperationSuccessResponse, Error, number>({
    mutationFn: async (permissionId: number) =>
      await request(`${sport}/o/${organizationId}/users/${userId}/permissions/${permissionId}`, {
        method: "DELETE",
      }),
    onSuccess: (response: DefaultOperationSuccessResponse, permissionId: number) => {
      if (isDefaultOperationSucceeded(response)) {
        queryClient.setQueryData<UserTransformed>(
          ["permission-users", sport, organizationId, userId],
          (oldUser: UserTransformed | undefined) => {
            if (oldUser) {
              const filterByPermissionId = (permission: { permissionId: number }) => permission.permissionId !== permissionId;
              return {
                ...oldUser,
                permissions: oldUser.permissions.filter(filterByPermissionId),
                userBasedPermissions: oldUser.userBasedPermissions.filter(filterByPermissionId),
                roleBasedPermissions: oldUser.roleBasedPermissions.filter(filterByPermissionId),
              };
            }
          },
        );
      }
    },
  });
};

export default useDeleteUserPermission;
